import { Icon, MockAvatar, MockFrame, MockRail, MockSidebar, cn, useRotator } from '@nexa/ui';
import { useCopy } from '@/core/i18n';
import { AgentPane } from './panes/AgentPane.tsx';
import { ChatPane } from './panes/ChatPane.tsx';
import { KanbanPane } from './panes/KanbanPane.tsx';

const PANES = [ChatPane, KanbanPane, AgentPane];

/**
 * Mock app 3 pane của hero — `template.html:645`.
 *
 * Ba khác biệt so với thiết kế, TẤT CẢ là hành vi — hình thức không đổi một pixel (§6.2):
 *   1. Ba chấm là `<button>` thật ⇒ dừng và điều khiển được ⇒ đạt WCAG 2.2.2. Thiết kế để ba
 *      `<span>` trơ (dòng 1409) chỉ đổi width/background.
 *   2. Rê chuột hoặc focus vào khung ⇒ dừng. Khung nhảy giữa lúc đang đọc là thứ không ai muốn.
 *   3. Tab ẩn và `prefers-reduced-motion` ⇒ không chạy (do `useRotator` lo, kể cả khi người dùng
 *      bật giữa chừng).
 *
 * Vùng chạm của chấm cao 44px và trong suốt, còn thứ nhìn thấy vẫn là thanh 4px y hệt thiết kế:
 * đạt tap target mà không phóng to đồ hoạ.
 *
 * Pane không hiện dùng `inert` (React 19) chứ không phải `hidden`: `hidden` giết luôn transition
 * mờ dần mà thiết kế có, còn `opacity:0` trần thì vẫn Tab vào được và screen reader vẫn đọc —
 * đúng lỗi của bản thiết kế. `inert` gỡ khỏi cây tương tác mà vẫn giữ được hiệu ứng.
 *
 * Cả khối `aria-hidden`: đây là ẢNH MINH HOẠ sản phẩm. Nội dung thật của section nằm ở h1 + lead
 * phía trên; đọc lại toàn bộ giao diện giả cho người dùng screen reader là nhiễu thuần tuý.
 */
export function HeroMockApp() {
  const copy = useCopy();
  const mock = copy.hero.mock;
  const rotator = useRotator(PANES.length);
  const activeIndex = rotator.index;

  return (
    <div
      data-testid="hero-mock"
      onMouseEnter={() => {
        rotator.setPaused(true);
      }}
      onMouseLeave={() => {
        rotator.setPaused(false);
      }}
      // `Capture` để bắt được focus của chính 3 chấm và của mọi thứ bên trong pane: người dùng
      // bàn phím cũng phải dừng được, không chỉ người dùng chuột.
      onFocusCapture={() => {
        rotator.setPaused(true);
      }}
      onBlurCapture={() => {
        rotator.setPaused(false);
      }}
    >
      <div className="mb-4 flex items-center justify-center gap-2.5">
        {copy.hero.paneLabels.map((label, index) => (
          <button
            key={label}
            type="button"
            aria-label={label}
            aria-pressed={index === activeIndex}
            onClick={() => {
              rotator.goTo(index);
            }}
            className="grid place-items-center px-1 py-5 -my-5"
          >
            <span
              className={cn(
                'block h-1 rounded-pill transition-all [transition-duration:var(--duration-reveal)]',
                // HÌNH HỌC (`w-2.5` ⇄ `w-[26px]`) ⇒ soft-curve, không phải `ease` mặc định.
                '[transition-timing-function:var(--ease-out-soft)]',
                index === activeIndex ? 'w-[26px] bg-white' : 'w-2.5 bg-[var(--on-dark-fill-35)]',
              )}
            />
          </button>
        ))}
      </div>

      {/*
        `decorative` chứ không phải thuộc tính ẩn viết tay: bản đầu viết thuộc tính có dấu gạch
        ngang, `MockFrameProps` không khai nó nên React bỏ qua — `typecheck` vẫn xanh vì TypeScript
        loại thuộc tính gạch ngang khỏi phép kiểm thừa-thuộc-tính. Kết quả là 1299 ký tự UI giả
        (gồm cả ký tự phím tắt) bị screen reader đọc ngay sau `h1`, và rotator đổi pane mỗi 3.6s
        nên nội dung còn đổi dưới con trỏ đọc.
      */}
      <MockFrame urlLabel={mock.urlBar} decorative>
        {/*
            Chiều cao khung ĐO chứ không chọn. Thiết kế khoá 452px ở artboard 1440 và ta giữ đúng
            số đó từ `lg`. Dưới `lg` phải CAO HƠN — nghe ngược nhưng đúng: khung hẹp thì chữ trong
            bong bóng wrap nhiều dòng hơn.

            Đo phần bị cắt của vùng tin nhắn (bản `vi`, dài hơn `en` nên nó là ca chặn):
              360px thiếu 101px · 390-1023px thiếu 80px · từ 1024px thiếu 6px
            ⇒ 482 / 460 / 452. Sáu pixel còn lại ở `lg` là lề dưới của bong bóng cuối chứ không
            phải chữ — giữ 452 để khớp artboard thay vì nới cho tròn số.

            Vì sao KHÔNG để chiều cao chạy theo nội dung: ba pane cao khác nhau, mà rotator đổi
            pane mỗi 3.6s ⇒ trang nhảy chiều cao ba lần mỗi chu kỳ. Đó chính là lý do thiết kế
            khoá cứng chiều cao.
          */}
        <div className="grid h-[482px] grid-cols-1 bg-surface-mock sm:h-[460px] lg:h-[452px] lg:grid-cols-[60px_232px_1fr]">
          <MockRail
            workspaces={[
              { label: 'N', tint: 'brand' },
              { label: 'NE', tint: 'brand-soft' },
              { label: 'AL', tint: 'success' },
            ]}
            icons={['messages-square', 'kanban', 'library', 'sparkles', 'search', 'bell']}
          />

          <MockSidebar workspace={mock.workspace} searchLabel={mock.jumpTo}>
            <div className="mb-2 ml-1 text-micro leading-[normal] font-bold tracking-wide text-ink-400">
              {mock.groupChannels}
            </div>
            <div className="flex flex-col gap-0.5">
              {mock.sidebar.channels.map((channel) => (
                <div
                  key={channel.name}
                  className={cn(
                    // `leading-[normal]` khớp thiết kế: mock là ảnh chụp UI sản phẩm dày đặc và
                    // thiết kế KHÔNG khai line-height cho các hàng này. Token của ta là 1.55 cho
                    // chữ đọc trên trang; áp nó vào đây làm mỗi hàng cao thêm 3.5px, tám hàng
                    // thành 30px và cả sidebar tụt xuống.
                    'flex items-center gap-2 text-body-sm leading-[normal]',
                    channel.state === 'active' &&
                      'rounded-field bg-brand-50 px-[9px] py-[7px] font-semibold text-brand-600',
                    channel.state === 'nested' &&
                      'py-[5px] pr-[9px] pl-7 text-caption text-ink-mock-500',
                    channel.state === 'idle' && 'rounded-field px-[9px] py-[7px] text-ink-700',
                  )}
                >
                  {/*
                    Kênh có `tag` là kênh đang phát trực tiếp — icon `mic` của nó tô xanh
                    (dòng 661). Suy từ DỮ LIỆU chứ không so tên kênh: so tên là gắn hình thức
                    vào một chuỗi mà người viết copy có thể đổi bất cứ lúc nào.
                  */}
                  <Icon
                    name={channel.icon}
                    size={13}
                    className={channel.tag === undefined ? 'text-current' : 'text-success-fg'}
                  />
                  <span className="truncate">{channel.name}</span>
                  {channel.badge !== undefined && (
                    <span
                      className={cn(
                        'ml-auto rounded-pill px-1.5 py-px text-nano',
                        channel.state === 'active'
                          ? 'bg-brand-600 text-white'
                          : 'bg-ink-100 text-ink-600',
                      )}
                    >
                      {channel.badge}
                    </span>
                  )}
                  {channel.tag !== undefined && (
                    <span className="ml-auto text-nano font-extrabold tracking-label text-success-fg">
                      {channel.tag}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 mb-2 ml-1 text-micro leading-[normal] font-bold tracking-wide text-ink-400">
              {mock.groupAgents}
            </div>
            <div className="flex flex-col gap-0.5">
              {mock.sidebar.agents.map((agent) => (
                <div
                  key={agent.name}
                  className="flex items-center gap-2 rounded-field px-[9px] py-1.5 text-body-sm leading-[normal] text-ink-700"
                >
                  <MockAvatar initials={agent.initials} tint={agent.tint} size={18} />
                  <span className="truncate">{agent.name}</span>
                </div>
              ))}
            </div>
          </MockSidebar>

          <div className="relative text-left">
            {PANES.map((Pane, index) => (
              <div
                key={copy.hero.paneLabels[index]}
                data-testid="hero-pane"
                inert={index !== activeIndex}
                className={cn(
                  'absolute inset-0 transition-[opacity,transform] [transition-duration:var(--duration-reveal)]',
                  // `opacity` đi CÙNG `transform` ⇒ theo curve của transform để hai bên không lệch pha.
                  '[transition-timing-function:var(--ease-out-soft)]',
                  index === activeIndex ? 'opacity-100' : 'translate-y-2.5 opacity-0',
                )}
              >
                <Pane />
              </div>
            ))}
          </div>
        </div>
      </MockFrame>
    </div>
  );
}
